import { useState } from 'react';

import { FontSize, FontWeight, STATUSES } from 'commons';
import { Button, Input, Text } from 'components';
import { useFormik } from 'formik';
import { formValidations, messages, swrKeys } from 'helpers';
import { useAuth } from 'hooks';
import { consignmentService } from 'services';
import { mutate } from 'swr';
import { ConsignmentDetails } from 'types';
import * as Yup from 'yup';

import * as S from '../../styles';

interface Props {
  consignmentDetails: ConsignmentDetails;
  action: string;
}

export function CustomerForm({ consignmentDetails, action }: Props) {
  const [isLoading, setIsLoading] = useState(false);
  const { selectedStore, user } = useAuth();
  const { isAdmin } = user;
  const { name: store } = selectedStore;

  const isAvailableToCollect: boolean = (() => {
    const {
      READY_FOR_PICKUP_BY_DRIVER,
      READY_FOR_CUSTOMER_PICKUP_IN_STORE,
      CLARIFY_DELIVERY_FAIL,
      COLLECT_FAIL,
    } = STATUSES;

    return [
      READY_FOR_PICKUP_BY_DRIVER.value,
      READY_FOR_CUSTOMER_PICKUP_IN_STORE.value,
      CLARIFY_DELIVERY_FAIL.value,
      COLLECT_FAIL.value,
    ].some(status => consignmentDetails.status === status);
  })();

  const customerValidation = Yup.object().shape({
    name: Yup.string().required('Nome'),
    cpf: Yup.string().required('CPF'),
  });

  const formCollect = useFormik({
    initialValues: {
      name: '',
      cpf: '',
      deliveryMode: '',
      pickUpAuthCode: '',
    },

    validateOnBlur: false,
    validateOnChange: false,

    validationSchema: customerValidation,
    validate: values => {
      formValidations.validationSchema({
        schema: customerValidation,
        values,
      });
    },

    onSubmit: async values => {
      setIsLoading(true);

      if (!store) {
        return messages([{ errorCode: 'FRONT-RE-STORE-NOT-FOUND' }]);
      }

      const response = await consignmentService.postCollect({
        consignmentCode: consignmentDetails.code,
        store,
        action,
        name: values.name,
        cpf: values.cpf,
        deliveryMode: null,
        pickUpAuthCode: values.pickUpAuthCode,
      });

      if (response?.status === 200) {
        messages([
          { errorCode: 'FRONT-RE-CONSIGNMENT_COLLECTED' },
          { subject: consignmentDetails.code, subjectType: 'consignmentCode' },
        ]);

        mutate(
          swrKeys.consignmentDetails({
            consignmentCode: consignmentDetails.code,
          })
        );
        mutate(swrKeys.consignments({ key: store }));
      } else {
        setIsLoading(false);
      }
    },
  });

  return (
    <S.Form onSubmit={formCollect.handleSubmit}>
      <S.Wrapper>
        <S.InputWrapper>
          <Text size={FontSize.font12} weight={FontWeight.bold}>
            Nome da Cliente/ Pessoa Autorizada
          </Text>

          <Input
            name="name"
            placeholder="Digite o nome completo"
            onChange={formCollect.handleChange}
            value={formCollect.values.name}
            status={formCollect.errors.name ? 'error' : 'default'}
            disabled={isAdmin}
          />
        </S.InputWrapper>

        <S.InputWrapper $small>
          <Text size={FontSize.font12} weight={FontWeight.bold}>
            CPF da Cliente/ Pessoa Autorizada
          </Text>

          <Input
            name="cpf"
            placeholder="xxx.xxx.xxx-xx"
            type="tel"
            onChange={formCollect.handleChange}
            value={formCollect.values.cpf}
            mask="999.999.999-99"
            status={formCollect.errors.cpf ? 'error' : 'default'}
            disabled={isAdmin}
          />
        </S.InputWrapper>
      </S.Wrapper>

      <S.Wrapper>
        <S.InputWrapper>
          <Text size={FontSize.font12} weight={FontWeight.bold}>
            Número de Autorização
          </Text>

          <div className="hint-container">
            <div className="input">
              <Input
                name="pickUpAuthCode"
                placeholder="Digite o código"
                onChange={formCollect.handleChange}
                value={formCollect.values.pickUpAuthCode}
                status={formCollect.errors.pickUpAuthCode ? 'error' : 'default'}
                disabled={isAdmin}
              />
            </div>

            <Text size={FontSize.font12}>
              Opcional quando a pessoa é a cliente do pedido
            </Text>
          </div>
        </S.InputWrapper>

        <S.ButtonWrapper>
          {isAvailableToCollect ? (
            <Button full type="submit" disabled={isLoading || isAdmin}>
              {isLoading ? 'Enviando...' : 'Registrar Retirada'}
            </Button>
          ) : (
            <Text size={FontSize.font12}>
              Aguarde o pedido ficar disponível para confirmar a coleta
            </Text>
          )}
        </S.ButtonWrapper>
      </S.Wrapper>
    </S.Form>
  );
}
